import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, ListGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPlus, faRobot, faTrash } from '@fortawesome/free-solid-svg-icons';
import { AxiosError } from 'axios';
import ReactTextareaAutosize from 'react-textarea-autosize';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { useBranchContext } from 'providers/BranchProvider';
import { useAuth } from 'hooks/useAuth';
import axiosInstance from 'utils/axiosInterceptors';
import { consumeSse } from 'utils/sseClient';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';

interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ConversationSummary {
  researchConversationId: number;
  title: string;
  updatedOn: string;
}

interface ConversationDetail extends ConversationSummary {
  messages: Array<{
    researchConversationMessageId: number;
    role: 'user' | 'assistant';
    content: string;
  }>;
}

interface ResearchChatResult {
  type: 'result';
  text?: string;
  conversationId?: number;
  title?: string;
}

interface Suggestion {
  label: string;
  prompt: string;
  roles: readonly string[];
}

const SUGGESTIONS: Suggestion[] = [
  {
    label: 'Parts',
    prompt: 'What brake pad fits a Toyota Vios, and do we have it in stock?',
    roles: FeatureRoles.inventorySearch
  },
  {
    label: 'Sales',
    prompt: 'Summarize our sales for the last 7 days.',
    roles: FeatureRoles.salesInvoice
  },
  {
    label: 'Purchasing',
    prompt: 'Which purchase orders are still open?',
    roles: FeatureRoles.purchaseOrder
  },
  {
    label: 'Shop',
    prompt: 'Which vehicle jobs are still open?',
    roles: FeatureRoles.vehicles
  },
  {
    label: 'Payroll',
    prompt: 'Who clocked in recently, and what was their latest net pay?',
    roles: FeatureRoles.humanResources
  }
];

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const ResearchChat = () => {
  const { token } = useAuth();
  const { selectedBranch } = useBranchContext();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [status, setStatus] = useState('Looking through records...');
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestRef = useRef(0);

  const suggestions = useMemo(
    () => SUGGESTIONS.filter(item => hasAnyRole(token?.roles, item.roles)).slice(0, 4),
    [token?.roles]
  );

  const loadConversations = useCallback(async () => {
    const { data } = await axiosInstance.get<ConversationSummary[]>('/api/v1/research-chat/conversations');
    setConversations(data);
  }, []);

  useEffect(() => {
    void loadConversations().catch(() => setError('Could not load chats'));
  }, [loadConversations]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, status, isLoading]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const openConversation = async (conversationId: number) => {
    abortRef.current?.abort();
    requestRef.current += 1;
    setActiveId(conversationId);
    setError(null);
    setIsLoading(false);
    setIsLoadingChat(true);
    try {
      const { data } = await axiosInstance.get<ConversationDetail>(
        `/api/v1/research-chat/conversations/${conversationId}`
      );
      setTurns(
        data.messages.map(message => ({
          id: String(message.researchConversationMessageId),
          role: message.role,
          content: message.content
        }))
      );
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) {
        setConversations(current =>
          current.filter(item => item.researchConversationId !== conversationId)
        );
        setActiveId(null);
        setTurns([]);
      }
      setError('Could not open that chat');
    } finally {
      setIsLoadingChat(false);
    }
  };

  const startNewChat = () => {
    abortRef.current?.abort();
    requestRef.current += 1;
    setActiveId(null);
    setTurns([]);
    setDraft('');
    setError(null);
    setIsLoading(false);
  };

  const deleteConversation = async (conversationId: number) => {
    try {
      await axiosInstance.delete(`/api/v1/research-chat/conversations/${conversationId}`);
      setConversations(current =>
        current.filter(item => item.researchConversationId !== conversationId)
      );
      if (activeId === conversationId) {
        startNewChat();
      }
    } catch {
      setError('Could not delete that chat');
    }
  };

  const rememberConversation = (conversationId: number, title: string) => {
    setActiveId(conversationId);
    setConversations(current => {
      const existing = current.find(item => item.researchConversationId === conversationId);
      const next = {
        researchConversationId: conversationId,
        title: title || existing?.title || 'New chat',
        updatedOn: new Date().toISOString()
      };
      return [next, ...current.filter(item => item.researchConversationId !== conversationId)];
    });
  };

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || isLoading) return;

    const myRequest = ++requestRef.current;
    const conversationAtSend = activeId;
    setTurns(current => [...current, { id: newId(), role: 'user', content }]);
    setDraft('');
    setError(null);
    setIsLoading(true);
    setStatus('Looking through records...');

    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      await consumeSse<ResearchChatResult>(
        '/api/v1/research-chat/stream',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId: conversationAtSend,
            content,
            branchDomain: selectedBranch?.branchDomain ?? null
          }),
          signal: abortController.signal
        },
        {
          onEvent: (type, data) => {
            if (requestRef.current !== myRequest || type !== 'conversation') return;
            if (!('conversationId' in data) || typeof data.conversationId !== 'number') return;
            const title = 'title' in data && typeof data.title === 'string' ? data.title : 'New chat';
            rememberConversation(data.conversationId, title);
          },
          onProgress: (_stage, message) => {
            if (requestRef.current === myRequest && message) setStatus(message);
          },
          onResult: data => {
            if (requestRef.current !== myRequest) return;
            if (typeof data.conversationId === 'number') {
              rememberConversation(data.conversationId, data.title || 'New chat');
            }
            setTurns(current => [
              ...current,
              { id: newId(), role: 'assistant', content: data.text || 'No answer came back.' }
            ]);
          },
          onError: (_code, message) => {
            if (requestRef.current !== myRequest) return;
            setError(message || 'Research failed');
          }
        }
      );
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      if (requestRef.current === myRequest) setError('Research failed');
    } finally {
      if (requestRef.current === myRequest) setIsLoading(false);
    }
  };

  return (
    <div>
      <PageBreadcrumb
        items={[
          { label: 'Home', url: '/' },
          { label: 'AI Assistant', active: true }
        ]}
      />
      <h2 className="mb-3">AI Assistant</h2>
      <div className="d-flex gap-3 align-items-stretch" style={{ minHeight: '70vh' }}>
        <Card className="flex-shrink-0" style={{ width: 280 }}>
          <Card.Header className="d-grid">
            <Button variant="primary" onClick={startNewChat}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              New chat
            </Button>
          </Card.Header>
          <ListGroup variant="flush" className="overflow-auto" style={{ maxHeight: '62vh' }}>
            {conversations.length === 0 && (
              <ListGroup.Item className="text-body-secondary small">No chats yet</ListGroup.Item>
            )}
            {conversations.map(conversation => (
              <ListGroup.Item
                key={conversation.researchConversationId}
                action
                active={conversation.researchConversationId === activeId}
                onClick={() => void openConversation(conversation.researchConversationId)}
                className="d-flex align-items-start gap-2"
              >
                <span className="flex-grow-1 text-truncate">{conversation.title}</span>
                <Button
                  variant="link"
                  size="sm"
                  className={`p-0 ${conversation.researchConversationId === activeId ? 'text-white' : 'text-body-secondary'}`}
                  title="Delete chat"
                  onClick={event => {
                    event.stopPropagation();
                    void deleteConversation(conversation.researchConversationId);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>

        <Card className="flex-grow-1">
          <Card.Body style={{ minHeight: 420, maxHeight: '62vh', overflowY: 'auto' }}>
            {isLoadingChat ? (
              <div className="text-center text-body-secondary py-5">
                <Spinner animation="border" size="sm" className="me-2" />
                Opening chat...
              </div>
            ) : turns.length === 0 ? (
              <div className="text-center text-body-secondary py-5">
                <FontAwesomeIcon icon={faRobot} size="2x" className="mb-3" />
                <p className="mb-3">
                  Start a chat about parts or the business. Each chat keeps its own history.
                  Answers only include records your role can already open.
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {suggestions.map(item => (
                    <Button
                      key={item.label}
                      variant="outline-primary"
                      size="sm"
                      onClick={() => void send(item.prompt)}
                    >
                      {item.prompt}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              turns.map(turn => (
                <div
                  key={turn.id}
                  className={`d-flex mb-3 ${turn.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  <div
                    className={`rounded px-3 py-2 ${turn.role === 'user' ? 'bg-primary text-white' : 'bg-body-secondary'}`}
                    style={{ maxWidth: '75%', whiteSpace: 'pre-wrap' }}
                  >
                    {turn.content}
                  </div>
                </div>
              ))
            )}

            {isLoading && (
              <div className="d-flex align-items-center text-body-secondary mb-3">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>{status}</span>
              </div>
            )}
            <div ref={bottomRef} />
          </Card.Body>
          {error && (
            <div className="px-3">
              <Alert variant="danger" className="py-2">
                <small>{error}</small>
              </Alert>
            </div>
          )}
          <Card.Footer>
            <Form
              onSubmit={event => {
                event.preventDefault();
                void send(draft);
              }}
            >
              <div className="d-flex gap-2 align-items-end">
                <ReactTextareaAutosize
                  minRows={1}
                  maxRows={5}
                  className="form-control"
                  placeholder="Ask a follow-up, or start a new chat from the list..."
                  value={draft}
                  disabled={isLoading || isLoadingChat}
                  onChange={event => setDraft(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void send(draft);
                    }
                  }}
                />
                <Button type="submit" disabled={isLoading || isLoadingChat || !draft.trim()}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
              </div>
            </Form>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
};

export default ResearchChat;
